import { format, formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'

/**
 * Returns the display name for a client as it should appear everywhere in the UI and on documents.
 * - Company: returns the company name as-is.
 * - Individual: prepends designation (e.g. "M. & Mme") if present, otherwise returns name.
 */
export function formatClientName(
  name: string | null | undefined,
  designation: string | null | undefined,
  isCompany: boolean = false
): string {
  const n = (name ?? '').trim()
  if (isCompany || !designation?.trim()) return n
  return `${designation.trim()} ${n}`.trim()
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

// Format date
export function formatDate(date: string | Date | undefined, formatStr: string = 'dd/MM/yyyy'): string {
  if (!date) return '-'
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return '-'
  return format(dateObj, formatStr, { locale: fr })
}

// Format relative time (e.g., "il y a 2 heures")
export function formatRelativeTime(date: string | Date | undefined): string {
  if (!date) return '-'
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return '-'
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: fr })
}

// Format dimensions
export function formatDimensions(
  length?: number,
  depth?: number,
  height?: number
): string {
  const parts = [length, depth, height].filter((v) => v !== undefined && v > 0)
  return parts.join(' × ') + ' cm'
}

// Calculate TVA
export function calculateTVA(
  priceHT: number,
  constructionType: 'neuf' | 'renovation' | 'co_contractant',
  isElectro: boolean = false
): { tva: number; ttc: number; rate: number } {
  // co_contractant = 0% TVA, renovation = 6% TVA (or 21% for electro), neuf = 21% TVA
  let rate = 0.21 // Default neuf
  
  if (constructionType === 'co_contractant') {
    rate = 0 // No TVA for co-contractant
  } else if (constructionType === 'renovation' && !isElectro) {
    rate = 0.06 // 6% for renovation (except electro)
  } else if (isElectro) {
    rate = 0.21 // Always 21% for electro
  }
  
  const tva = priceHT * rate
  const ttc = priceHT + tva
  return { tva, ttc, rate }
}

// Format relative date (future: "Dans N jours", today: "Aujourd'hui", past: "Il y a N jours")
export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((date.getTime() - today.getTime()) / 86_400_000)
  if (diffDays === 0) return "Aujourd'hui"
  if (diffDays > 0) return `Dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`
  return `Il y a ${Math.abs(diffDays)} jour${Math.abs(diffDays) > 1 ? 's' : ''}`
}

// Generate project ID
export function generateProjectId(): string {
  const year = new Date().getFullYear()
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, '0')
  return `${year}-${random}`
}

// Generate element ID
export function generateElementId(): string {
  return `el-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// Human-readable file size (e.g. "1,2 MB"). Used by the project documents list.
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 o'
  const units = ['o', 'Ko', 'Mo', 'Go']
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / Math.pow(1024, i)
  const rounded = i === 0 ? value : Math.round(value * 10) / 10
  return `${rounded.toLocaleString('fr-BE')} ${units[i]}`
}
